import InputDisplay from 'components/atoms/DisplayName/input-display';
import SimulationFilter from 'components/atoms/SimulationFilter';
import { UserContext } from 'contexts/ConfigContext';
import { priceSimulatorDataContext } from 'contexts/PriceSimulatorContext';
import React, { useContext, useMemo, useState } from 'react';
import { getConfig } from 'utils/helperMethods';
import { DateRangePriceSimulator } from '../../DateRangePriceSimulator';
import { Popover } from 'antd';
import dayjs from 'dayjs';
import Calender, { DateRangeType } from 'components/molecules/Calender';
import { default as CalenderIcon } from 'assets/svg/Calender';
import KPIAddTable from '../../KPIAddTable/KPIAddTable';
import _ from 'lodash';
import Loader from 'components/molecules/Loader';
import { ConfigKeys, priceSimulatorErrorMessages } from 'utils/Enums';
import { CustomSelect } from 'CustomComponentCss';
import DownSvg from 'assets/svg/Down';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PSR_DATA_SOURCE_CHANGED, PSR_NAME_UPDATED, PSR_PRICE_EFFECTIVE_DATE_CHANGED } from 'utils/mixpanelEventNames';
import { FilterContextData } from 'contexts/FilterContext';

const currentDate = new Date();

const GoalStep = () => {
    const {
        title,
        setTitle,
        priceEffectiveDate,
        setPriceEffectiveDate,
        tempPriceEffectiveDate,
        setTempPriceEffectiveDate,
        viewMode,
        scenarioNamesData,
        fetchScenarioLoading,
        selectedDataSource,
        setSelectedDataSource,
        simulationDateRange,
    } = useContext(priceSimulatorDataContext);
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const { calenderStartDay } = useContext(FilterContextData);
    const { dataSources } = getConfig(ConfigKeys.PRICE_SIMULATOR, appConfig);

    const [isOpen, setIsOpen] = useState(false);

    const dateLabel = useMemo(() => {
        let startLabel = 'Start Date';
        if (priceEffectiveDate[1]) startLabel = dayjs(new Date(priceEffectiveDate[1])).format('MM/DD/YYYY');
        return { startLabel };
    }, [priceEffectiveDate]);

    const handleOpenChange = (newOpen: boolean, isClickOutside: boolean = true) => {
        setIsOpen(newOpen);
        if (!newOpen && !isClickOutside) {
            //only save the date when clicked on set date button
            setPriceEffectiveDate(tempPriceEffectiveDate);
            trackEventMixpanel(PSR_PRICE_EFFECTIVE_DATE_CHANGED, {
                priceEffectiveDate: tempPriceEffectiveDate[1],
            });
        } else if (!newOpen) {
            //if clicked outside dont save the change,
            setTempPriceEffectiveDate(priceEffectiveDate);
        }
    };

    const handleDateChange = (range: DateRangeType) => {
        setTempPriceEffectiveDate([...range]);
    };

    // compute weeks difference
    const diffInWeeks = dayjs(simulationDateRange[1])?.diff(dayjs(simulationDateRange[0]), 'week', true);

    return (
        <div id="price-sim-goal-step">
            {fetchScenarioLoading ? (
                <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 top-1/2">
                    <Loader />
                </div>
            ) : (
                <>
                    <InputDisplay
                        inputValue={title}
                        defaultDisplayName={title || 'Scenario Name'}
                        maxCharacter={50}
                        maxLengthValidationMessage={priceSimulatorErrorMessages.MAX_LENGTH}
                        minLengthValidationMessage={priceSimulatorErrorMessages.MIN_LENGTH}
                        alreadyExistMessage={priceSimulatorErrorMessages.DUPLICATE_NAME}
                        alreadyExist={scenarioNamesData?.includes(title)}
                        disable={viewMode}
                        minCharacter={3}
                        textChangeHandler={(value: string) => {
                            setTitle(value);
                            trackEventMixpanel(PSR_NAME_UPDATED, {
                                value,
                            });
                        }}
                    />
                    <div className="text-promoColour font-normal mt-1 mb-2">
                        Please select products and attributes from the global filter
                    </div>
                    <SimulationFilter />
                    <div className="font-medium font-md my-1"> Basic Inputs</div>
                    <div className="flex border p-1 pl-2 rounded border-borderGrey mb-2">
                        <div className="flex flex-col mr-12" id="price-sim-data-source">
                            <span className="mb-2 font-medium"> Data Source</span>
                            <div className="flex">
                                <span className="mr-6 font-greyFont flex flex-col dropdown-width-multi-select">
                                    <span className="font-xxs">
                                        Data Source
                                        <span style={{ color: '#cb3d3d' }}>*</span>
                                    </span>
                                    <CustomSelect
                                        className="dropdown-height uniqSelGFDataSourceDropdown  width-full"
                                        suffixIcon={<DownSvg />}
                                        value={selectedDataSource?.value || null}
                                        options={dataSources}
                                        onChange={(val: any, item: any) => {
                                            setSelectedDataSource(item);
                                            trackEventMixpanel(PSR_DATA_SOURCE_CHANGED, {
                                                dataSource: item.label,
                                            });
                                        }}
                                        placeholder="Select Data Source"
                                        disabled={viewMode}
                                    />
                                </span>
                            </div>
                        </div>

                        <div className="flex flex-col mr-12" id="price-sim-period">
                            <span className="mb-2 font-medium"> Simulation Period</span>
                            <div className="flex">
                                <span className="mr-6 font-greyFont flex flex-col">
                                    <div className="flex justify-between font-xxs">
                                        <span className="font-xxs">
                                            Date Range<span style={{ color: '#cb3d3d' }}>*</span>
                                        </span>
                                        {!isNaN(diffInWeeks) && (
                                            <span>{`${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'}`}</span>
                                        )}
                                    </div>
                                    <DateRangePriceSimulator />
                                </span>
                            </div>
                        </div>

                        {/* COMMENTING OTHER FIELDS FOR MVP */}
                        <div className="flex flex-col">
                            <span className="mb-2  font-medium"> Other Inputs</span>
                            <div className="flex">
                                {/* TODO : we will be adding this component in future */}
                                {/* <span className="mr-6 font-greyFont flex flex-col">
                                        <span className="font-xxs">Import Promotions (Optional)</span>
                                        <CustomSelect
                                            // disabled={commonLoading || dropdownLoading}
                                            className="dropdown-height  width-full"
                                            suffixIcon={<DownSvg />}
                                            value={selectPPG?.value}
                                            options={attributeTypesList}
                                            onChange={(val: any, item: any) => {
                                                setSelectPPG(item);
                                            }}
                                            placeholder="Select Promo Plan"
                                        />
                                    </span> */}
                                <span className="font-greyFont flex flex-col" id="price-sim-price-eff-date">
                                    <span className="font-xxs">Price Effective Date (Optional)</span>
                                    <Popover
                                        trigger="click"
                                        open={!viewMode && isOpen}
                                        onOpenChange={handleOpenChange}
                                        placement={'right'}
                                        content={
                                            <Calender
                                                minYear={currentDate.getFullYear()}
                                                startYear={currentDate.getFullYear()}
                                                maxYear={currentDate.getFullYear() + 2}
                                                disabledDates={[dayjs(currentDate).format('MM-DD-YYYY'), null]}
                                                startDay={calenderStartDay}
                                                range={tempPriceEffectiveDate}
                                                onChange={handleDateChange}
                                                onFooterClick={() => handleOpenChange(false, false)}
                                                singleDateSelect={true}
                                            />
                                        }
                                    >
                                        <div className="flex justify-center items-center w-36">
                                            <div
                                                className={` ${
                                                    !viewMode ? 'cursor-pointer' : ''
                                                } w-full flex justify-start gap-1 py-1 items-center px-1 border border-purpleFont rounded`}
                                            >
                                                <div className="flex w-full">
                                                    <span
                                                        className={`${
                                                            priceEffectiveDate[1] && !isOpen
                                                                ? 'text-subHeaderColor'
                                                                : dateLabel.startLabel !== 'Start Date'
                                                                ? 'text-greyFont'
                                                                : 'text-borderGrey'
                                                        }  font-xs`}
                                                    >
                                                        {dateLabel.startLabel}
                                                    </span>
                                                </div>
                                                <CalenderIcon />
                                            </div>
                                        </div>
                                    </Popover>
                                </span>
                            </div>
                        </div>
                    </div>
                    {/* KPI editable table */}
                    <KPIAddTable />
                </>
            )}
        </div>
    );
};

export default GoalStep;
